import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MinistryDetail from './pages/MinistryDetail';
import RequestReimbursement from './pages/RequestReimbursement';
import AdminMinistries from './pages/AdminMinistries';
import AdminReimbursements from './pages/AdminReimbursements';
import AdminUsers from './pages/AdminUsers';
import ArchivedMinistries from './pages/ArchivedMinistries';
import ImportOfferings from './pages/ImportOfferings';
import PayerDashboard from './pages/PayerDashboard';
import MinistryAssets from './pages/MinistryAssets';
import AssetForm from './pages/AssetForm';
import Vote from './pages/Vote';
import Telao from './pages/Telao';
import Elections from './pages/Elections';
import ElectionDashboard from './pages/ElectionDashboard';
import ElectionReport from './pages/ElectionReport';
import { getMinistries } from './services/api';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="loading">Carregando...</div>;
  }
  
  return user ? children : <Navigate to="/login" />;
};

const AppContent = () => {
  const { user, isPayer, isSecretary } = useAuth();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [ministries, setMinistries] = useState([]);

  useEffect(() => {
    if (user && !isPayer && !isSecretary) {
      loadMinistries();
    }
  }, [user, selectedYear, isPayer, isSecretary]);

  const loadMinistries = async () => {
    try {
      const response = await getMinistries(selectedYear);
      
      // Filter ministries based on user permissions
      let filteredMinistries = response.data;
      
      if (user.role !== 'admin' && user.ministries) {
        const userMinistryIds = user.ministries.map(m => m._id || m);
        filteredMinistries = response.data.filter(m => 
          userMinistryIds.includes(m._id)
        );
      }
      
      setMinistries(filteredMinistries);
    } catch (error) {
      console.error('Erro ao carregar ministérios:', error);
    }
  };

  return (
    <>
      {user && (
        <Navbar 
          ministries={ministries}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
      )}
      
      <Routes>
        {/* Rotas públicas (sem login, sem Navbar) */}
        <Route path="/votar" element={<Vote />} />
        <Route path="/telao" element={<Telao />} />

        <Route path="/login" element={
          user
            ? (isPayer ? <Navigate to="/payer" /> : (isSecretary ? <Navigate to="/admin/votacoes" /> : <Navigate to="/" />))
            : <Login />
        } />
        
        <Route path="/" element={
          <PrivateRoute>
            {isPayer
              ? <Navigate to="/payer" />
              : (isSecretary ? <Navigate to="/admin/votacoes" /> : <Dashboard selectedYear={selectedYear} onYearChange={setSelectedYear} />)}
          </PrivateRoute>
        } />
        
        <Route path="/payer" element={
          <PrivateRoute>
            {isPayer ? <PayerDashboard /> : <Navigate to="/" />}
          </PrivateRoute>
        } />
        
        <Route path="/ministry/:id" element={
          <PrivateRoute>
            <MinistryDetail selectedYear={selectedYear} />
          </PrivateRoute>
        } />
        
        <Route path="/ministry/:id/assets" element={
          <PrivateRoute>
            <MinistryAssets selectedYear={selectedYear} />
          </PrivateRoute>
        } />
        
        <Route path="/ministry/:id/assets/new" element={
          <PrivateRoute>
            <AssetForm selectedYear={selectedYear} />
          </PrivateRoute>
        } />
        
        <Route path="/ministry/:id/assets/:assetId/edit" element={
          <PrivateRoute>
            <AssetForm selectedYear={selectedYear} />
          </PrivateRoute>
        } />
        
        <Route path="/ministry/:ministryId/request-reimbursement" element={
          <PrivateRoute>
            <RequestReimbursement />
          </PrivateRoute>
        } />
        
        <Route path="/admin/ministries" element={
          <PrivateRoute>
            <AdminMinistries />
          </PrivateRoute>
        } />
        
        <Route path="/admin/reimbursements" element={
          <PrivateRoute>
            <AdminReimbursements />
          </PrivateRoute>
        } />
        
        <Route path="/admin/users" element={
          <PrivateRoute>
            <AdminUsers />
          </PrivateRoute>
        } />
        
        <Route path="/admin/archived" element={
          <PrivateRoute>
            <ArchivedMinistries />
          </PrivateRoute>
        } />
        
        <Route path="/admin/import-offerings" element={
          <PrivateRoute>
            <ImportOfferings />
          </PrivateRoute>
        } />

        <Route path="/admin/votacoes" element={
          <PrivateRoute>
            <Elections />
          </PrivateRoute>
        } />

        <Route path="/admin/votacoes/:id" element={
          <PrivateRoute>
            <ElectionDashboard />
          </PrivateRoute>
        } />

        <Route path="/admin/votacoes/:id/relatorio" element={
          <PrivateRoute>
            <ElectionReport />
          </PrivateRoute>
        } />
        
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
        <ToastContainer
          position="top-right"
          autoClose={8000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
